import type {
  Operator,
  OperatorCreate,
  OperatorUpdate,
} from "@/providers/smarta-accounts/accounts-api-client";

import { isRecord } from "@/v1/utils/record";
import { client } from "./client";

/**
 * The generated client returns the parsed error body,
 * `{ success, error: { message } }`, not an `Error`. A caller shows
 * `error.message` in a toast, so turn the body into an `Error` with the
 * server's message. Anything else keeps the fallback message.
 */
const toOperatorError = (error: unknown, fallback: string): Error => {
  if (error instanceof Error) return error;
  const message =
    isRecord(error) && isRecord(error.error) && error.error.message;
  return new Error(typeof message === "string" && message ? message : fallback);
};

/**
 * The name every operator picker, the Action log and the assignee columns
 * show: the person's display name (the preferred name, else the given names,
 * else the family name). Only the operator management table shows the full
 * name, through its separate name fields.
 */
export const operatorName = (operator: Operator): string =>
  operator.person.displayName;

/**
 * Fetch every operator matching the filter. The endpoint has no paging since
 * SMT-1448, so one request returns the whole list.
 */
const fetchOperators = async (disabled?: "false"): Promise<Operator[]> => {
  const { data, error } = await client.GET("/api/operators", {
    params: { query: disabled ? { disabled } : {} },
  });
  if (error || !data?.data) {
    throw toOperatorError(error, "Malformed list-operators response");
  }
  return data.data;
};

/**
 * List the enabled operators from smarta-accounts through the generated client.
 * Mirrors how the banking module calls its accounts-backed endpoints — no
 * hand-written axios path.
 *
 * `disabled: false` excludes disabled operators — the list endpoint returns
 * both by default, but the picker and the filters must only show people who
 * can still be assigned work.
 */
export const listOperators = async (): Promise<Operator[]> =>
  fetchOperators("false");

/**
 * List operators including disabled ones, by omitting the `disabled` filter
 * (the endpoint returns both by default).
 *
 * Only for *reading back* an operator id that was written in the past, such as
 * rendering a name in the Action log, and for the operator management table.
 * An assignment made years ago can point at someone who has since left, and
 * those are exactly the entries a reader opens the log for; `listOperators`
 * would leave them as raw ids. Do NOT use this to populate a picker or a
 * filter — a disabled operator must not be offered as a new assignee.
 */
export const listOperatorsIncludingDisabled = async (): Promise<Operator[]> =>
  fetchOperators();

/** Create an operator via `POST /api/operators`. */
export const createOperator = async (
  payload: OperatorCreate
): Promise<Operator> => {
  const { data, error } = await client.POST("/api/operators", {
    body: payload,
  });
  if (error || !data?.data) {
    throw toOperatorError(error, "Malformed create-operator response");
  }
  return data.data;
};

/** Update an operator via `PATCH /api/operators/{id}`. */
export const updateOperator = async (
  id: string,
  payload: OperatorUpdate
): Promise<Operator> => {
  const { data, error } = await client.PATCH("/api/operators/{id}", {
    params: { path: { id } },
    body: payload,
  });
  if (error || !data?.data) {
    throw toOperatorError(error, "Malformed update-operator response");
  }
  return data.data;
};

/**
 * Disable an operator via `POST /api/operators/{id}/disable`. A disabled
 * operator cannot log in and is not offered as an assignee. Idempotent.
 */
export const disableOperator = async (id: string): Promise<Operator> => {
  const { data, error } = await client.POST("/api/operators/{id}/disable", {
    params: { path: { id } },
  });
  if (error || !data?.data) {
    throw toOperatorError(error, "Malformed disable-operator response");
  }
  return data.data;
};

/** Enable a disabled operator via `POST /api/operators/{id}/enable`. */
export const enableOperator = async (id: string): Promise<Operator> => {
  const { data, error } = await client.POST("/api/operators/{id}/enable", {
    params: { path: { id } },
  });
  if (error || !data?.data) {
    throw toOperatorError(error, "Malformed enable-operator response");
  }
  return data.data;
};
